/**
 * Утилиты для работы с датами
 */

/**
 * Форматирует дату в строку YYYY-MM-DD
 * @param date - Дата для форматирования
 * @returns Строка в формате YYYY-MM-DD
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Нормализует дату, устанавливая время в 00:00:00
 * @param date - Дата для нормализации (Date, string или timestamp)
 * @returns Новый объект Date с временем 00:00:00
 */
export const GetDate = (date: Date | string | number): Date => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
};

/**
 * Определяет период письма (эта неделя, этот месяц или название месяца)
 * @param date - Дата письма
 * @param currentDate - Текущая дата
 * @param locale - Локаль для форматирования
 * @param translations - Объект с переводами (thisWeek, thisMonth)
 * @returns Строка с названием периода
 */
export const getPeriodLabel = (
    date: Date,
    currentDate: Date,
    locale: string,
    translations: { thisWeek: string; thisMonth: string },
): string => {
    const mailDate = GetDate(date);
    const today = GetDate(currentDate);

    // Начало текущей недели (понедельник)
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(today.getDate() + diff);

    // Начало текущего месяца
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    if (mailDate >= startOfWeek) {
        return translations.thisWeek;
    } else if (mailDate >= startOfMonth) {
        return translations.thisMonth;
    } else {
        const formatter = new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric',
        });
        const formatted = formatter.format(mailDate);
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }
};

/**
 * Форматирует диапазон дат для отображения
 * @param from - Начальная дата
 * @param to - Конечная дата
 * @param locale - Локаль для форматирования
 * @param translations - Объект с переводами (dateFrom, dateTo)
 * @returns Отформатированная строка с диапазоном дат
 */
export const formatDateRange = (
    from?: Date,
    to?: Date,
    locale?: string,
    translations?: { dateFrom: string; dateTo: string },
): string => {
    const dateFormatter = new Intl.DateTimeFormat(locale || 'en', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });

    if (from && to) {
        return `${dateFormatter.format(from)} - ${dateFormatter.format(to)}`;
    } else if (from && translations) {
        return `${translations.dateFrom} ${dateFormatter.format(from)}`;
    } else if (to && translations) {
        return `${translations.dateTo} ${dateFormatter.format(to)}`;
    }
    return '';
};

/**
 * Форматирует время, убирая секунды если они есть
 * @param time - Время в формате HH:mm или HH:mm:ss
 * @returns Время в формате HH:mm
 */
export const formatTime = (time: string): string => {
    // Убираем секунды, если они есть (формат HH:mm:ss -> HH:mm)
    return time.split(':').slice(0, 2).join(':');
};

/**
 * Вычисляет разницу между двумя временами
 * @param startTime - Время начала в формате HH:mm или HH:mm:ss
 * @param endTime - Время окончания в формате HH:mm или HH:mm:ss
 * @returns Объект с часами, минутами и общим количеством минут
 */
export const calculateTimeDifference = (
    startTime: string,
    endTime: string,
): { hours: number; minutes: number; totalMinutes: number; totalHours: number } => {
    const start = formatTime(startTime);
    const end = formatTime(endTime);

    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    // 00:00 считаем как полночь (конец суток), т.е. бронь до полуночи
    const endTotalMinutes = endHours === 0 && endMinutes === 0 ? 24 * 60 : endHours * 60 + endMinutes;
    const diffMinutes = endTotalMinutes - startTotalMinutes;

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    const totalHours = diffMinutes / 60;

    return {
        hours,
        minutes,
        totalMinutes: diffMinutes,
        totalHours,
    };
};

/**
 * Форматирует продолжительность времени с использованием переводов
 * @param startTime - Время начала в формате HH:mm или HH:mm:ss
 * @param endTime - Время окончания в формате HH:mm или HH:mm:ss
 * @param translations - Объект с переводами (hours, minutes)
 * @returns Отформатированная строка продолжительности
 */
export const formatDuration = (
    startTime: string,
    endTime: string,
    translations: { hours: string; minutes: string },
): string => {
    const { hours, minutes } = calculateTimeDifference(startTime, endTime);

    if (hours > 0 && minutes > 0) {
        return `${hours} ${translations.hours} ${minutes} ${translations.minutes}`;
    } else if (hours > 0) {
        return `${hours} ${translations.hours}`;
    } else {
        return `${minutes} ${translations.minutes}`;
    }
};
